/**
 * One process-wide handle on the navigation guard that is mounted right now.
 *
 * The mapping workspace model runs *above* <MainLayout>, so a React context
 * published by the layout can never reach it, and a context published by the
 * workspace can never reach the layout's workflow steps. Every control -
 * workflow step, workspace tab, profile selection, profile load - therefore
 * asks this service, and the service routes to the single mounted guard.
 *
 * Registration is tokenised the same way host-ribbon ownership is: a guard that
 * unmounts after a newer one has registered cannot tear down its successor.
 */

import { runNavigationTransaction, type RequestNavigation } from './navigationTransaction';

export interface NavigationGuardToken {
  readonly epoch: number;
}

interface NavigationGuardRegistration extends NavigationGuardToken {
  readonly requestNavigation: RequestNavigation;
}

let lastClaimedEpoch = 0;
let registered: NavigationGuardRegistration | null = null;

export const registerNavigationGuard = (
  requestNavigation: RequestNavigation
): NavigationGuardToken => {
  lastClaimedEpoch += 1;
  registered = { epoch: lastClaimedEpoch, requestNavigation };
  return { epoch: lastClaimedEpoch };
};

export const isCurrentNavigationGuard = (token: NavigationGuardToken | null): boolean =>
  Boolean(token) && registered?.epoch === token!.epoch;

/** A stale guard releasing after a newer one registered is a no-op. */
export const releaseNavigationGuard = (token: NavigationGuardToken): boolean => {
  if (!isCurrentNavigationGuard(token)) {
    return false;
  }

  registered = null;
  return true;
};

/**
 * With no guard mounted there is no editor holding unsaved edits, so there is
 * nothing to prompt about - but the revision policy still decides. A dirty or
 * in-flight revision blocks rather than slipping through unguarded.
 */
const withoutMountedGuard: RequestNavigation = (request, commit, options) =>
  runNavigationTransaction(
    request,
    commit,
    { isDirty: false, resolveUnsaved: async () => 'blocked' },
    options
  );

export const requestGuardedNavigation: RequestNavigation = (request, commit, options) =>
  (registered?.requestNavigation ?? withoutMountedGuard)(request, commit, options);

/** Test seam: forget a registration a previous render left behind. */
export const resetNavigationGuardRegistry = (): void => {
  registered = null;
};
